import React, { useState, useCallback } from 'react';
import { FaEnvelope, FaSignInAlt, FaGlobeAmericas, FaTimes, FaBullhorn, FaCrown, FaShoppingCart, FaTicketAlt, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import GoogleSignInButton, { getGoogleClientId } from './GoogleSignInButton';
import { emailSignup, trackEvent } from '../api/client';

// Email-first registration.
//
// Signing up asks for one thing: an email address. We create the account with
// no password (demo tier) and send a one-time sign-in link; the traveller
// finishes by opening that link. This mirrors the "Save plan by email" capture
// on the tryout preview so that every place a visitor can create an account
// behaves the same way. Existing password accounts still sign in on the Login
// page, and Google remains a one-tap alternative here.
const Register = ({ onGoogleAuth, onSwitchToLogin, onNavigate }) => {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Bot trap field
  const [pendingPlan, setPendingPlan] = useState(null); // Plan selected from pricing page
  const [googleLoading, setGoogleLoading] = useState(false);

  const googleEnabled = !!getGoogleClientId();

  // Get referral code and lifetime code from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get('ref') || '';
  const lifetimeCode = urlParams.get('lifetime') || '';

  // Check for pending plan from pricing page on mount
  React.useEffect(() => {
    const storedPlan = sessionStorage.getItem('pending_plan');
    if (storedPlan) {
      try {
        setPendingPlan(JSON.parse(storedPlan));
      } catch (e) {
        console.error('Error parsing pending plan:', e);
      }
    }
  }, []);

  // Google hands us a verified ID token; the server does the rest. The referral
  // and lifetime codes from the URL, plus marketing consent, must ride along -
  // otherwise a Google sign-up silently loses them.
  const handleGoogleCredential = useCallback(async (credential) => {
    setErrorMessage('');
    setGoogleLoading(true);
    try {
      await onGoogleAuth(credential, {
        marketingConsent,
        referralCode,
        lifetimeCode,
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setErrorMessage(detail || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }, [onGoogleAuth, marketingConsent, referralCode, lifetimeCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Honeypot check - if filled, silently reject (bot detected)
    if (honeypot) {
      console.log('Bot detected via honeypot');
      return;
    }

    const address = email.trim();
    if (!address) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(address)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      await emailSignup(address, { referralCode, marketingConsent });
      try { trackEvent('account_email_signup', { surface: 'register' }); } catch (_) {}
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setErrorMessage(detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-scale-in">
      <div className="bg-white rounded-2xl shadow-elevated p-8">
        {/* Logo & Welcome */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <img src="/logo.png" alt="ItinerarySeek" className="h-16 w-auto" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-secondary-500 to-primary-500 bg-clip-text text-transparent mb-2">
            Start Your Journey
          </h1>
          <p className="text-neutral-600">Create your free account with just an email</p>
          {lifetimeCode && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-full text-sm text-purple-700">
              <FaCrown className="text-purple-500" />
              <span>Lifetime Navigator Membership</span>
            </div>
          )}
          {referralCode && !lifetimeCode && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full text-sm text-emerald-700">
              <FaGlobeAmericas className="text-emerald-500" />
              Referred by a friend &middot; Code: <strong>{referralCode}</strong>
            </div>
          )}
          {/* Pending Plan Banner - shows when user came from pricing page */}
          {pendingPlan && !submitted && (
            <div className="mt-4 bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-xl p-4 text-left">
              <div className="flex items-start gap-3">
                {pendingPlan.type === 'payg' ? (
                  <FaTicketAlt className="text-secondary-500 text-xl flex-shrink-0 mt-0.5" />
                ) : (
                  <FaShoppingCart className="text-primary-500 text-xl flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="font-semibold text-neutral-800">
                    {pendingPlan.type === 'payg'
                      ? 'You\'re signing up to purchase a trip credit'
                      : `You're signing up for the ${pendingPlan.tierId === 'individual' ? 'Solo' : pendingPlan.tierId === 'group' ? 'Group' : pendingPlan.tierId} plan`
                    }
                  </p>
                  <p className="text-sm text-neutral-600 mt-1">
                    Open the sign-in link we email you, and we'll take you to checkout to finish.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {submitted ? (
          /* Success state - link sent, nothing more to type */
          <div className="text-center animate-scale-in">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-4">
              <FaCheckCircle className="text-green-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-neutral-800 mb-2">Check your email</h2>
            <p className="text-neutral-600">
              We sent a sign-in link to <span className="font-semibold">{email.trim()}</span>.
              Open it to finish setting up your free account &mdash; no password needed.
            </p>
            <p className="text-sm text-neutral-500 mt-4">
              Didn't get it? Check your spam folder, or{' '}
              <button
                type="button"
                onClick={() => { setSubmitted(false); }}
                className="text-secondary-600 font-semibold hover:underline"
              >
                try again
              </button>
              .
            </p>
          </div>
        ) : (
          <>
            {/* Error Banner */}
            {errorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 animate-slide-down">
                <p className="text-sm">{errorMessage}</p>
              </div>
            )}

            {/* Google Sign-Up - a one-tap alternative. Renders nothing when no
                client ID is configured, leaving email as the only path. */}
            {googleEnabled && (
              <div className="mb-6">
                <GoogleSignInButton
                  mode="signup"
                  onCredential={handleGoogleCredential}
                  disabled={googleLoading || loading}
                />

                {googleLoading && (
                  <div className="flex items-center justify-center gap-2 mt-3 text-sm text-neutral-600">
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    Setting up your account...
                  </div>
                )}

                {/* Divider */}
                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-neutral-200"></div>
                  <span className="text-xs font-medium text-neutral-400 uppercase tracking-wide">or</span>
                  <div className="flex-1 h-px bg-neutral-200"></div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - hidden from users, visible to bots */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
              />

              {/* Email Field - the only thing we ask for */}
              <div>
                <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                  <FaEnvelope className="text-primary-500" />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  autoComplete="email"
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                    errors.email
                      ? 'border-red-500 bg-red-50'
                      : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-secondary-100'
                  } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={loading}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Marketing Consent (Optional) */}
              <div className="mt-3">
                <label className={`flex items-start gap-3 cursor-pointer ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    disabled={loading}
                    className="mt-1 w-5 h-5 text-purple-500 border-2 border-neutral-300 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <span className="text-sm text-neutral-700 flex items-start gap-2">
                    <FaBullhorn className="text-purple-500 mt-0.5 flex-shrink-0" />
                    <span>
                      I'd like to receive emails about new features, travel tips, and special offers.
                      <span className="text-neutral-500 ml-1">(Optional)</span>
                    </span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-secondary text-white py-3 px-6 rounded-lg font-semibold text-lg shadow-secondary hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 mt-2 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending your link...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Create your free account
                  </>
                )}
              </button>

              {/* Inline terms acceptance - continuing is the acceptance, mirroring
                  the Google path. Keeps the form to a single field. */}
              <p className="text-xs text-neutral-500 text-center leading-relaxed">
                No password or card required. By continuing you agree to our{' '}
                <button
                  type="button"
                  className="text-primary-600 hover:text-primary-700 underline"
                  onClick={() => setShowTermsModal(true)}
                >
                  Terms of Service
                </button>
                {' '}and{' '}
                <button
                  type="button"
                  className="text-primary-600 hover:text-primary-700 underline"
                  onClick={() => setShowPrivacyModal(true)}
                >
                  Privacy Policy
                </button>
                .
              </p>
            </form>
          </>
        )}

        {/* Switch to Login */}
        <div className="mt-6 text-center">
          <p className="text-neutral-600">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              disabled={loading}
              className="text-secondary-600 font-semibold hover:text-secondary-700 hover:underline transition-colors inline-flex items-center gap-1 disabled:opacity-50"
            >
              <FaSignInAlt />
              Sign In
            </button>
          </p>
        </div>
      </div>

      {/* Internal Links for SEO */}
      <div className="mt-6 pt-4 border-t border-white/20">
        <p className="text-sm text-white/70 text-center">
          Explore:{' '}
          <a href="/about" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('about'); }} className="text-white/90 hover:text-white hover:underline">About</a>
          {' · '}
          <a href="/pricing" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('pricing'); }} className="text-white/90 hover:text-white hover:underline">Pricing</a>
          {' · '}
          <a href="/faq" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('faq'); }} className="text-white/90 hover:text-white hover:underline">FAQ</a>
          {' · '}
          <a href="/blog" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('blog'); }} className="text-white/90 hover:text-white hover:underline">Blog</a>
          {' · '}
          <a href="/contact" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('contact'); }} className="text-white/90 hover:text-white hover:underline">Contact</a>
        </p>
      </div>

      {/* Decorative Quote */}
      <p className="text-center text-white/80 mt-4 italic text-sm">
        "Adventure is worthwhile in itself." - Amelia Earhart
      </p>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTermsModal(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 bg-neutral-50">
              <h2 className="text-xl font-bold text-neutral-800">Terms of Service</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-2 hover:bg-neutral-200 rounded-lg transition-colors"
              >
                <FaTimes className="text-neutral-600" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <TermsOfService />
            </div>
            <div className="p-4 border-t border-neutral-200 bg-neutral-50">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacyModal(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 bg-neutral-50">
              <h2 className="text-xl font-bold text-neutral-800">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="p-2 hover:bg-neutral-200 rounded-lg transition-colors"
              >
                <FaTimes className="text-neutral-600" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <PrivacyPolicy />
            </div>
            <div className="p-4 border-t border-neutral-200 bg-neutral-50">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
